import { patient } from "@/src/mocks/patient";
import type { ParsedOrder, SoapSection } from "./mockAiService";

/**
 * Auditor Agent — live documentation-gap checks.
 *
 * Runs the same class of checks as the standalone Auditor Agent, but against
 * the note the doctor is writing RIGHT NOW: the live SOAP text, the drafted
 * orders, and the patient's diagnoses / labs / allergies / MAR. Each finding
 * carries a one-click fix that reuses the app's existing SOAP/order handlers.
 *
 * Demonstration logic only — rule-based on mock data. At go-live these checks
 * move server-side against the real record and evidence store.
 */

export type AuditFlag = "missing-doc" | "overclaim" | "underclaim" | "insufficient-data";
export type AuditSeverity = "high" | "medium" | "low";

export type AuditFix =
  | { kind: "focus"; field: SoapSection; label: string }
  | { kind: "soap"; field: SoapSection; text: string; title: string; evidence: string; label: string }
  | { kind: "order"; order: ParsedOrder; title: string; evidence: string; label: string };

export type AuditFinding = {
  id: string;
  flag: AuditFlag;
  severity: AuditSeverity;
  title: string;
  detail: string;
  gap: string;
  recommendation: string;
  grounding: string;
  fix?: AuditFix;
};

const SECTION_LABEL: Record<SoapSection, string> = {
  subjective: "Subjective",
  objective: "Objective",
  assessment: "Assessment",
  plan: "Plan",
};

export function runAuditorChecks(input: {
  soap: Record<SoapSection, string>;
  orders: ParsedOrder[];
}): AuditFinding[] {
  const { soap, orders } = input;
  const findings: AuditFinding[] = [];

  const noteText = Object.values(soap).join("\n").toLowerCase();
  // Treatments actually ordered/administered — from the MAR and drafted orders
  // ONLY (not the note prose, so "review transfusion threshold" in the Plan is
  // not mistaken for a transfusion having been given).
  const medOrderText = [
    ...patient.mar,
    ...orders.map((o) => `${o.label} ${o.details}`),
  ]
    .join("\n")
    .toLowerCase();
  const labs = [...patient.labs.CBC, ...patient.labs.Chem7];
  const dxText = patient.diagnoses.map((d) => `${d.shortLabel} ${d.label}`).join(" ").toLowerCase();

  // 1) Empty SOAP sections — a complete note needs all four on an active day.
  (["subjective", "objective", "assessment", "plan"] as SoapSection[]).forEach((field) => {
    if (!soap[field] || !soap[field].trim()) {
      findings.push({
        id: `soap-empty-${field}`,
        flag: "missing-doc",
        severity: field === "subjective" || field === "objective" ? "high" : "medium",
        title: `${SECTION_LABEL[field]} section is empty`,
        detail: `The ${SECTION_LABEL[field]} part of today's SOAP note has no text. A complete progress note needs all four SOAP sections on an active admission day.`,
        gap: `${SECTION_LABEL[field]} not documented.`,
        recommendation: `Document the ${SECTION_LABEL[field]} for today's note.`,
        grounding: "SOAP completeness standard",
        fix: { kind: "focus", field, label: `Go to ${SECTION_LABEL[field]}` },
      });
    }
  });

  // 2) Allergy vs medication conflict (penicillin ↔ beta-lactam), unexplained.
  const penicillinAllergy = patient.allergies.some((a) => /penicillin/i.test(a));
  const betaLactam = /amoxicillin|co-amoxiclav|augmentin|ampicillin|cephalexin|ceftriaxone|cefotaxime|penicillin/i;
  const allergyReconciled = /allergy reviewed|allergy checked|tolerat|de-?label/i.test(noteText);
  if (penicillinAllergy && betaLactam.test(medOrderText) && !allergyReconciled) {
    findings.push({
      id: "allergy-conflict",
      flag: "missing-doc",
      severity: "high",
      title: "Beta-lactam on the medication list against a penicillin allergy",
      detail:
        "The record lists a penicillin allergy, but a beta-lactam antibiotic (amoxicillin) is on the medication list with no note reconciling the two. Either the allergy needs re-checking or the antibiotic should change.",
      gap: "A beta-lactam ordered against a documented penicillin allergy, unexplained.",
      recommendation: "Add a note reconciling the penicillin allergy with the antibiotic, or change the drug.",
      grounding: "Allergy / order-consistency standard",
      fix: {
        kind: "soap",
        field: "plan",
        title: "Allergy reconciliation",
        evidence: "Allergy list · MAR review",
        label: "Add reconciliation note",
        text:
          "Penicillin allergy reviewed against the current beta-lactam order; reconcile the antibiotic choice or re-take the allergy history.",
      },
    });
  }

  // 3) Anemia diagnosis with no iron therapy or transfusion ordered.
  const hasAnemiaDx = /anemia|anaemia/.test(dxText) || /anemia|anaemia/.test(noteText);
  const hb = labs.find((l) => /^hb$/i.test(l.name));
  const hasIronOrTransfusion = /ferrous|iron|feso4|transfus|packed red|\bprc\b/i.test(medOrderText);
  if (hasAnemiaDx && !hasIronOrTransfusion) {
    findings.push({
      id: "anemia-no-treatment",
      flag: "missing-doc",
      severity: "high",
      title: hb
        ? `Anemia (Hb ${hb.value}) with no iron therapy or transfusion ordered`
        : "Anemia with no iron therapy or transfusion ordered",
      detail:
        "Anemia is on the problem list" +
        (hb ? ` and Hb is ${hb.value} ${hb.unit}` : "") +
        ", but there is no ferrous/iron order and no transfusion on the MAR or in the orders. The expected treatment is missing or undocumented.",
      gap: "A documented anemia with no treatment evidence (no iron, no transfusion).",
      recommendation: "Order iron therapy or a transfusion as indicated, or document the treatment already given.",
      grounding: "Diagnosis ↔ treatment consistency",
      fix: {
        kind: "order",
        title: "Iron therapy for anemia",
        evidence: "Anemia (D64.9) · CBC",
        label: "Draft ferrous order",
        order: {
          id: `aud-fe-${Date.now()}`,
          category: "medication",
          label: "Ferrous sulfate 200 mg PO tid",
          details: "For iron-deficiency anemia; recheck Hb",
          lane: "continuous",
          stat: false,
          source: "agent",
        },
      },
    });
  }

  // 4) Warfarin active but no INR/PT result or order.
  const onWarfarin = /warfarin/i.test(medOrderText);
  const hasINR = labs.some((l) => /inr|^pt$/i.test(l.name)) || /\binr\b|pt\/inr/i.test(medOrderText + " " + noteText);
  if (onWarfarin && !hasINR) {
    findings.push({
      id: "warfarin-no-inr",
      flag: "missing-doc",
      severity: "high",
      title: "Warfarin active but no INR result on file",
      detail:
        "Warfarin is on the active medication list, but there is no INR/PT result in the labs and none ordered. Warfarin dosing cannot be shown to be safe without an INR.",
      gap: "Anticoagulant given with no INR monitoring documented.",
      recommendation: "Order an INR/PT to support the warfarin dose, or attach the latest result.",
      grounding: "Medication-monitoring standard",
      fix: {
        kind: "order",
        title: "INR monitoring for warfarin",
        evidence: "MAR · warfarin active",
        label: "Draft PT/INR order",
        order: {
          id: `aud-inr-${Date.now()}`,
          category: "lab",
          label: "PT/INR",
          details: "Warfarin monitoring — tomorrow 08:00",
          lane: "one-day",
          stat: false,
          source: "agent",
        },
      },
    });
  }

  // 5) Abnormal labs not addressed anywhere in the note.
  const addressTerms: Record<string, RegExp> = {
    Cr: /creatinine|renal|kidney|\baki\b|\bcr\b/i,
    Mg: /magnesium|\bmg\b/i,
    Hb: /anemia|anaemia|h(?:a)?emoglobin|\bhb\b|transfus/i,
    Hct: /anemia|anaemia|h(?:a)?ematocrit|\bhct\b/i,
    K: /potassium|hypokal|hyperkal/i,
    Na: /sodium|hyponatr|hypernatr/i,
  };
  labs
    .filter((l) => l.abnormal)
    .forEach((l) => {
      const term = addressTerms[l.name];
      const addressed = term ? term.test(noteText) : false;
      if (!addressed) {
        findings.push({
          id: `lab-unaddressed-${l.name}`,
          flag: "missing-doc",
          severity: l.name === "Cr" ? "medium" : "low",
          title: `Abnormal ${l.name} (${l.value} ${l.unit}) not addressed in the note`,
          detail: `${l.name} is flagged abnormal at ${l.value} ${l.unit}, but the note's Assessment/Plan does not mention it. Active abnormal results should be addressed in the progress note.`,
          gap: `Abnormal ${l.name} not addressed in the Assessment or Plan.`,
          recommendation: `Add a line to the Assessment addressing the ${l.name} result.`,
          grounding: "Active-problem documentation standard",
          fix: {
            kind: "soap",
            field: "assessment",
            title: `Address abnormal ${l.name}`,
            evidence: `Lab · ${l.name} ${l.value} ${l.unit}`,
            label: "Add to Assessment",
            text: `${l.name} ${l.value} ${l.unit} noted (abnormal); will monitor and manage as clinically indicated.`,
          },
        });
      }
    });

  const rank: Record<AuditSeverity, number> = { high: 0, medium: 1, low: 2 };
  findings.sort((a, b) => rank[a.severity] - rank[b.severity]);
  return findings;
}
